import { memo } from 'react'

import { Box, styled, Tooltip, Typography } from '@mui/material'
import { AvcTask } from '@nativewaves/nw-cloud-sdk/content'
import { upperFirst } from 'lodash-es'
import { AnimatePresence, motion } from 'motion/react'
import { useTranslation } from 'react-i18next'

import { isVersionIrrelevantState } from 'domains/Workspaces/ContentFlows/AvcTasks'
import { getAvcTaskStateColor } from 'utils'

type SecondaryTitleBadgeProps = Partial<Pick<AvcTask, 'state' | 'health'>>

const SecondaryTitleBadge = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'state' && prop !== 'health',
})<SecondaryTitleBadgeProps>(({ theme, state, health }) => ({
  display: 'grid',
  placeItems: 'center',
  padding: 0,
  height: 32,
  width: 100,
  textAlign: 'center',
  borderRadius: 4,
  backgroundColor: state ? getAvcTaskStateColor(state, theme.palette.mode, health) : theme.palette.warning.dark,
  color: state ? theme.palette.getContrastText(getAvcTaskStateColor(state, theme.palette.mode, health)) : 'inherit',
}))

type DynamicBadeProps = Pick<AvcTask, 'activeVersionId' | 'versionId' | 'state' | 'health'>

const DynamicBadgeRaw = ({ activeVersionId, versionId, state, health }: DynamicBadeProps) => {
  const { t } = useTranslation(['common', 'entity', 'pages'])

  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch', columnGap: 1, mx: 2 }}>
      <SecondaryTitleBadge variant="heading-sm" state={state} health={health}>
        {upperFirst(state.toLowerCase())}
      </SecondaryTitleBadge>
      <AnimatePresence>
        {![...versionId].every((letter) => letter === '0') &&
          !!activeVersionId.length &&
          activeVersionId !== versionId &&
          !isVersionIrrelevantState(state) && (
            <motion.div key="version-badge" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <Tooltip title={t('pages:Workspace.taskConfigurationChangeSuccess')} placement="bottom" arrow>
                <SecondaryTitleBadge variant="heading-sm" noWrap>
                  {t('pages:Workspace.versionMismatch')}
                </SecondaryTitleBadge>
              </Tooltip>
            </motion.div>
          )}
      </AnimatePresence>
    </Box>
  )
}

export const DynamicBadge = memo(DynamicBadgeRaw)
